import { DomainError } from '../errors.js';
import { LATE_ENTRY_DAYS } from './reports.js';
import { VESSEL_CERTIFICATE_EXPIRING_DAYS } from './vessel-certificates.js';

/**
 * Şirketin PMS eşikleri (Settings ▸ PMS; plan M7 Task 21c; K27, K30, K32): yalnız uyarı ve rapor
 * eşikleri. Uyum kuralları (gelecek tarih, düşük okuma, günde 24 saat, onayda parola) ayar
 * değildir ve kapatılamaz [NR496 1.4.2].
 */
export interface PmsSettingsValues {
  /** Vadeye bu kadar gün kala "Due Soon" (PMS belgesi §21 "Due next 7 days"). */
  dueSoonDays: number;
  /** Yapılıştan bu kadar gün sonra girilen kayıt "Late Entries" raporunda (K27). */
  lateEntryDays: number;
  /** Gemi sertifikası bitişe bu kadar gün kala "Expiring" (K30). */
  certificateExpiringDays: number;
}

export const PMS_SETTINGS_DEFAULTS: Readonly<PmsSettingsValues> = {
  dueSoonDays: 7,
  lateEntryDays: LATE_ENTRY_DAYS,
  // Sertifika durumunun kendi varsayılanıyla aynı (ADR 0029 D7)
  certificateExpiringDays: VESSEL_CERTIFICATE_EXPIRING_DAYS,
};

export const PMS_SETTING_KEYS = [
  'dueSoonDays',
  'lateEntryDays',
  'certificateExpiringDays',
] as const;

/** Aralıklar: en az bir gün; Due Soon iki ay, geç giriş üç ay, sertifika altı ay. */
export const PMS_SETTING_LIMITS: Readonly<
  Record<keyof PmsSettingsValues, { min: number; max: number }>
> = {
  dueSoonDays: { min: 1, max: 60 },
  lateEntryDays: { min: 1, max: 90 },
  certificateExpiringDays: { min: 1, max: 180 },
};

/** Gönderilen eşikleri denetler; bilinmeyen alan ya da aralık dışı değer `INVALID_PMS_SETTINGS`. */
export function parsePmsSettings(input: Record<string, unknown>): Partial<PmsSettingsValues> {
  const out: Partial<PmsSettingsValues> = {};
  for (const [key, value] of Object.entries(input)) {
    if (!(PMS_SETTING_KEYS as readonly string[]).includes(key)) {
      throw new DomainError('INVALID_PMS_SETTINGS', key);
    }
    const limit = PMS_SETTING_LIMITS[key as keyof PmsSettingsValues];
    if (
      typeof value !== 'number' ||
      !Number.isInteger(value) ||
      value < limit.min ||
      value > limit.max
    ) {
      throw new DomainError('INVALID_PMS_SETTINGS', key);
    }
    out[key as keyof PmsSettingsValues] = value;
  }
  return out;
}
